import { useCallback, useEffect, useRef, useState } from "react";
import { Check, CheckCheck, Clock, AlertCircle, SendHorizontal, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";

const POLL_MS = 15000;

function Tick({ s }) {
  if (s === "read") return <CheckCheck className="h-3 w-3 text-sky-500" />;
  if (s === "delivered") return <CheckCheck className="h-3 w-3 text-slate-400" />;
  if (s === "sent") return <Check className="h-3 w-3 text-slate-400" />;
  if (s === "failed") return <AlertCircle className="h-3 w-3 text-red-500" />;
  return <Clock className="h-3 w-3 text-slate-400" />;
}

function Bubble({ m }) {
  const out = m.direction === "out";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`wa-bubble-${m.id}`}>
      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${out ? "bg-emerald-100 text-slate-900 rounded-br-sm" : "bg-white text-slate-900 border border-slate-200 rounded-bl-sm"}`}>
        {m.type === "template"
          ? <p className="flex items-center gap-1.5 text-slate-700"><FileText className="h-3.5 w-3.5" />Modello “{m.template}”{m.params?.length ? `: ${m.params.join(" · ")}` : ""}</p>
          : <p className="whitespace-pre-wrap break-words">{m.text}</p>}
        <p className="mt-1 flex items-center justify-end gap-1 text-[10px] text-slate-500">
          {out && m.created_by ? `${m.created_by.split("@")[0]} · ` : ""}{fmtDateTime(m.created_at)}{out && <Tick s={m.status} />}
        </p>
        {m.error && <p className="text-[10px] text-red-600">{m.error}</p>}
      </div>
    </div>
  );
}

export function WhatsAppChat({ trust, person, onTemplate, onActivity }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = useCallback(() => {
    api.get("/whatsapp/messages", { params: { trust_id: trust.id, subject_id: person.subject_id } }).then((r) => setItems(r.data.slice().reverse())).catch(() => {});
    onActivity?.();
  }, [trust.id, person.subject_id, onActivity]);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [items.length]);

  const reply = async () => {
    setBusy(true);
    try {
      await api.post("/whatsapp/send", { trust_id: trust.id, subject_id: person.subject_id, text });
      setText("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const canSend = person.consent && person.wa_id;

  return (
    <div className="flex flex-col h-[520px]" data-testid="wa-chat">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div>
          <p className="font-medium text-slate-900">{person.name}</p>
          <p className="text-[11px] text-slate-500">{person.wa_id ? `+${person.wa_id}` : "cellulare mancante"} · {person.window_open ? "finestra 24 ore aperta" : "finestra 24 ore chiusa: solo modelli"}</p>
        </div>
        <Button size="sm" variant="outline" disabled={!canSend} onClick={onTemplate} data-testid="wa-chat-template-btn"><FileText className="h-3.5 w-3.5 mr-1" />Modello</Button>
      </div>
      <div className="flex-1 overflow-y-auto bg-[#EFEAE2] px-4 py-4 space-y-2" data-testid="wa-chat-thread">
        {!items.length && <p className="text-center text-xs text-slate-500 py-10" data-testid="wa-chat-empty">Nessun messaggio con questo cliente</p>}
        {items.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={end} />
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2">
        <textarea rows={1} maxLength={4096} disabled={!person.window_open || !canSend} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && text.trim()) { e.preventDefault(); reply(); } }}
          placeholder={person.window_open ? "Scrivi una risposta…" : "Risposta libera disponibile solo entro 24 ore dall'ultimo messaggio del cliente"}
          className="flex-1 resize-none rounded-full border border-slate-300 px-4 py-2 text-sm outline-none focus:border-emerald-600 disabled:bg-slate-50 disabled:text-slate-400" data-testid="wa-chat-input" />
        <Button disabled={busy || !text.trim() || !person.window_open || !canSend} onClick={reply} className="rounded-full h-10 w-10 p-0 bg-emerald-600 hover:bg-emerald-700" aria-label="Invia" data-testid="wa-chat-send">
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
